import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Package, 
  Search, 
  Eye, 
  EyeOff, 
  Copy, 
  ExternalLink,
  CheckCircle,
  ChevronUp,
  ChevronDown,
  Lock,
  Wifi,
  WifiOff,
  AlertCircle,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { useOrderWebSocket } from '@/hooks/useOrderWebSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientServices() {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [viewExpanded, setViewExpanded] = useState(true);
  // Cancel order state
  const [cancelOrderDialog, setCancelOrderDialog] = useState(null);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  }, []);

  // WebSocket handler for real-time updates
  const handleWebSocketMessage = useCallback((message) => {
    console.log('[Services WS] Received:', message);
    
    if (message.type === 'order_provisioned' || message.type === 'service_ready') {
      toast.success(`🎉 Service Ready: ${message.data?.product_name || 'Your service'}`, {
        description: 'Click to view your login credentials',
        duration: 10000
      });
      fetchOrders();
    } else if (message.type === 'order_status_changed') {
      const statusText = message.data?.new_status?.replace('_', ' ');
      toast.info(`Service status changed to: ${statusText}`);
      fetchOrders();
    } else if (message.type === 'order_cancelled') {
      toast.warning(`Service cancelled: ${message.data?.product_name || ''}`);
      fetchOrders();
    } else if (message.type === 'order_activated') {
      toast.success(`Service activated: ${message.data?.product_name || ''}`);
      fetchOrders();
    }
  }, [fetchOrders]);

  const { isConnected: wsConnected } = useOrderWebSocket(handleWebSocketMessage);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status) {
      setFilterStatus(status);
    }
  }, [searchParams]);

  const statusCounts = {
    active: orders.filter(o => o.status === 'active').length,
    pending: orders.filter(o => o.status === 'pending').length,
    processing: orders.filter(o => o.status === 'processing').length,
    suspended: orders.filter(o => o.status === 'suspended').length,
    terminated: orders.filter(o => o.status === 'terminated').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
    cancellation_requested: orders.filter(o => o.status === 'cancellation_requested').length,
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.provisioning?.server_url?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const viewCredentials = (order) => {
    setSelectedOrder(order);
    setShowPassword(false);
    setCredentialsDialogOpen(true);
  };

  const handleCancelOrder = async (orderId) => {
    if (!cancelReason.trim()) {
      setCancelReasonError('Please provide a reason for cancellation');
      return;
    }
    setCancellingOrder(orderId);
    try {
      await axios.post(`${API}/client/orders/${orderId}/cancel`, { reason: cancelReason.trim() });
      toast.success('Cancellation request submitted. Admin will review it shortly.');
      setCancelOrderDialog(null);
      setCancelReason('');
      setCancelReasonError('');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit cancellation request');
    } finally {
      setCancellingOrder(null);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: 'bg-green-500', text: 'text-white', label: 'Active' },
      pending: { bg: 'bg-amber-500', text: 'text-white', label: 'Pending' },
      processing: { bg: 'bg-blue-500', text: 'text-white', label: 'Processing' },
      suspended: { bg: 'bg-orange-500', text: 'text-white', label: 'Suspended' },
      terminated: { bg: 'bg-red-500', text: 'text-white', label: 'Terminated' },
      cancelled: { bg: 'bg-slate-500', text: 'text-white', label: 'Cancelled' },
      cancellation_requested: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Cancel Requested' },
    };
    const style = styles[status] || styles.cancelled;
    return (
      <span className={`px-3 py-1 rounded-md text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    if (status === 'active') return <Lock className="h-8 w-8 text-green-500" />;
    if (status === 'cancelled') return <Lock className="h-8 w-8 text-slate-400" />;
    if (status === 'suspended') return <Lock className="h-8 w-8 text-orange-500" />;
    return <Lock className="h-8 w-8 text-slate-400" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getBillingText = (order) => {
    const amount = order.amount?.toLocaleString() || '0';
    const cycle = order.billing_cycle?.replace('_', ' ') || 'One Time';
    return `₹${amount} INR ${cycle.charAt(0).toUpperCase() + cycle.slice(1)}`;
  };

  return (
    <DashboardLayout role="client">
      <div className="flex gap-6">
        {/* Left Sidebar */}
        <div className="w-64 flex-shrink-0 space-y-4">
          {/* View Filter Card */}
          <Card className="bg-white border shadow-sm">
            <CardContent className="p-0">
              <button 
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
                onClick={() => setViewExpanded(!viewExpanded)}
              >
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-slate-600" />
                  <span className="font-medium text-slate-700">View</span>
                </div>
                {viewExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              
              {viewExpanded && (
                <div className="border-t">
                  {[
                    { key: 'all', label: 'All', count: orders.length },
                    { key: 'active', label: 'Active', count: statusCounts.active },
                    { key: 'pending', label: 'Pending', count: statusCounts.pending },
                    { key: 'processing', label: 'Processing', count: statusCounts.processing },
                    { key: 'suspended', label: 'Suspended', count: statusCounts.suspended },
                    { key: 'terminated', label: 'Terminated', count: statusCounts.terminated },
                    { key: 'cancellation_requested', label: 'Cancel Requested', count: statusCounts.cancellation_requested },
                    { key: 'cancelled', label: 'Cancelled', count: statusCounts.cancelled }
                  ].map((item) => (
                    <button
                      key={item.key}
                      className={`w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left border-b last:border-b-0 ${
                        filterStatus === item.key ? 'bg-blue-50 text-blue-700' : ''
                      }`}
                      onClick={() => setFilterStatus(item.key)}
                      data-testid={`filter-${item.key}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          filterStatus === item.key ? 'border-blue-600' : 'border-slate-300'
                        }`}>
                          {filterStatus === item.key && (
                            <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                          )}
                        </span>
                        <span className="text-sm">{item.label}</span>
                      </div>
                      <span className="text-sm font-medium text-slate-500">{item.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {/* Search and Info Bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <p className="text-sm text-slate-600">
                Showing 1 to {filteredOrders.length} of {filteredOrders.length} entries
              </p>
              {wsConnected ? (
                <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full" title="Real-time updates active">
                  <Wifi className="h-3 w-3" />
                  Live
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-full" title="Connecting...">
                  <WifiOff className="h-3 w-3" />
                  Offline
                </span>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64"
                data-testid="search-services"
              />
            </div>
          </div>

          {/* Services Table */}
          <Card className="bg-white border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">
                      <div className="flex items-center gap-1">
                        Product/Service
                        <ChevronDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">
                      <div className="flex items-center gap-1">
                        Pricing
                        <ChevronDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">
                      <div className="flex items-center gap-1">
                        Activation Date
                        <ChevronDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">
                      <div className="flex items-center gap-1">
                        Next Due Date
                        <ChevronDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">
                      <div className="flex items-center gap-1">
                        Status
                        <ChevronDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-nowrap">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-slate-500 text-nowrap">
                        <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                        <p className="font-medium">No services found</p>
                        <p className="text-sm mt-1">Your services will appear here once ordered</p>
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <tr 
                        key={order.id} 
                        className="border-b last:border-b-0 hover:bg-slate-50 transition-colors"
                        data-testid={`service-row-${order.id}`}
                      >
                        <td className="px-6 py-4 text-nowrap">
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0">
                              {getStatusIcon(order.status)}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{order.product_name}</p>
                              {['cancelled', 'cancellation_requested'].includes(order.status) ? (
                                <p className="text-sm text-slate-400 italic">—</p>
                              ) : order.provisioning?.server_url ? (
                                <a 
                                  href={order.provisioning.server_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline"
                                >
                                  {order.provisioning.server_url}
                                </a>
                              ) : (
                                <p className="text-sm text-slate-400">No URL assigned</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-nowrap">
                          <p className="text-sm text-slate-700">{getBillingText(order)}</p>
                        </td>
                        <td className="px-6 py-4 text-nowrap">
                          <p className="text-sm text-slate-700">
                            {order.status === 'active' && order.activated_at 
                              ? formatDate(order.activated_at)
                              : '-'}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-nowrap">
                          <p className="text-sm text-slate-700">
                            {order.status === 'active' && order.next_due_date 
                              ? formatDate(order.next_due_date)
                              : '-'}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-nowrap">
                          {getStatusBadge(order.status)}
                        </td>
                        <td className="px-6 py-4 text-nowrap">
                          <div className="flex items-center gap-2">
                            {order.status === 'active' && order.provisioning && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => viewCredentials(order)}
                                data-testid={`view-credentials-${order.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            {order.status === 'cancellation_requested' ? (
                              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-md font-medium">
                                Pending Review
                              </span>
                            ) : ['pending', 'active', 'processing', 'provisioned'].includes(order.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-600 hover:border-red-600 hover:text-white"
                                onClick={() => { setCancelOrderDialog(order); setCancelReason(''); setCancelReasonError(''); }}
                                data-testid={`cancel-order-btn-${order.id}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>

      {/* Credentials Dialog */}
      <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Service Login Credentials</DialogTitle>
          </DialogHeader>
          {selectedOrder?.provisioning && (
            <div className="space-y-4 py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">{selectedOrder.product_name}</span>
                </div>
              </div>
              
              {selectedOrder.provisioning.server_url && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-600">Server URL</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-100 rounded-lg px-3 py-2 font-mono text-sm break-all">
                      {selectedOrder.provisioning.server_url}
                    </div>
                    <Button 
                      size="icon" 
                      variant="outline"
                      onClick={() => copyToClipboard(selectedOrder.provisioning.server_url, 'Server URL')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="outline"
                      onClick={() => window.open(selectedOrder.provisioning.server_url, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {selectedOrder.provisioning.port && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-600">Port</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-100 rounded-lg px-3 py-2 font-mono text-sm">
                      {selectedOrder.provisioning.port}
                    </div>
                    <Button 
                      size="icon" 
                      variant="outline"
                      onClick={() => copyToClipboard(selectedOrder.provisioning.port, 'Port')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {selectedOrder.provisioning.username && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-600">Username</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-100 rounded-lg px-3 py-2 font-mono text-sm">
                      {selectedOrder.provisioning.username}
                    </div>
                    <Button 
                      size="icon" 
                      variant="outline"
                      onClick={() => copyToClipboard(selectedOrder.provisioning.username, 'Username')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {selectedOrder.provisioning.password && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-600">Password</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-100 rounded-lg px-3 py-2 font-mono text-sm">
                      {showPassword ? selectedOrder.provisioning.password : '••••••••••'}
                    </div>
                    <Button 
                      size="icon" 
                      variant="outline"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button 
                      size="icon" 
                      variant="outline"
                      onClick={() => copyToClipboard(selectedOrder.provisioning.password, 'Password')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {selectedOrder.provisioning.bios_ipmi_access && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-600">BIOS/IPMI Access</label>
                  <div className="bg-slate-100 rounded-lg px-3 py-2 text-sm whitespace-pre-wrap">
                    {selectedOrder.provisioning.bios_ipmi_access}
                  </div>
                </div>
              )}
              
              <div className="pt-4 border-t border-slate-200 text-xs text-slate-500">
                <p>Provisioned on: {new Date(selectedOrder.provisioning.provisioned_at).toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                })}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Order Confirmation Dialog */}
      <Dialog open={!!cancelOrderDialog} onOpenChange={(o) => { if (!o) { setCancelOrderDialog(null); setCancelReason(''); setCancelReasonError(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Request Order Cancellation</DialogTitle>
          </DialogHeader>
          {cancelOrderDialog && (
            <div className="space-y-4 pt-1">
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded border border-amber-200">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Submit Cancellation Request</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Your request for <strong>{cancelOrderDialog.product_name}</strong> will be reviewed by admin.
                  </p>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Reason for Cancellation *</label>
                <textarea
                  className={`w-full border rounded-md text-sm px-3 py-2 min-h-[72px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${cancelReasonError ? 'border-red-400' : 'border-slate-200'}`}
                  placeholder="Please explain why you want to cancel this service..."
                  value={cancelReason}
                  onChange={e => { setCancelReason(e.target.value); if (cancelReasonError) setCancelReasonError(''); }}
                  data-testid="cancel-reason-input"
                />
                {cancelReasonError && <p className="text-xs text-red-500">{cancelReasonError}</p>}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setCancelOrderDialog(null); setCancelReason(''); setCancelReasonError(''); }}>Keep Order</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => handleCancelOrder(cancelOrderDialog.id)}
                  disabled={cancellingOrder === cancelOrderDialog.id}
                  data-testid="confirm-cancel-service"
                >
                  {cancellingOrder === cancelOrderDialog.id
                    ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    : null}
                  Submit Request
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
